import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCoachingCapture } from '@/lib/api';
import { useScopedKey } from '@/lib/queryClient';
import type { DraftContext } from '@/features/coach-review/composerDrafts';
import { ownedRequest } from './useAssistContext';
import { captureMessage } from './assistPresentation';

/** Mounted only while visible. Fresh read on every return, never an old save
 * acknowledgment: drafting may have happened in the retained review form. */
export function CaptureReceipt({ owner, captureId }: { owner: DraftContext; captureId: string }) {
  const client = useQueryClient();
  const instance = useRef(crypto.randomUUID());
  const key = useScopedKey('coach-assist-capture-receipt', { captureId, actor: owner.actorId, club: owner.clubId, generation: owner.generation, instance: instance.current });
  const receipt = useQuery({ queryKey: key, queryFn: ({ signal }) => ownedRequest(owner, () => getCoachingCapture(captureId, { teamId: owner.teamId, seasonId: owner.seasonId }, signal)), retry: false, staleTime: 0, gcTime: 0, refetchInterval: q => q.state.error ? false : 2000 });
  useEffect(() => () => { void client.cancelQueries({ queryKey: key, exact: true }); client.removeQueries({ queryKey: key, exact: true }); }, [client, JSON.stringify(key)]);
  return <p role="status" className="text-sm">{receipt.isPending ? 'Saved privately. Checking current capture status…' : captureMessage(receipt.isError ? undefined : receipt.data?.capture.status)}</p>;
}
